import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { websiteApi } from '../api/website.api';
import { sectionApi, Section, Layout } from '../api/section.api';
import ContentEditor from '../components/ContentEditor';
import CategoryProductManager from '../components/CategoryProductManager';
import GalleryCategoryManager from '../components/GalleryCategoryManager';
import ProductImportExport from '../components/ProductImportExport';
import Modal from '../components/Modal';
import ImageUpload from '../components/ImageUpload';
import ThemeEditor from '../components/ThemeEditor';
import EditWebsiteSidebar, { EditWebsiteTab, EDIT_WEBSITE_TABS, LEGACY_TAB_REDIRECTS } from '../components/EditWebsiteSidebar';
import EnquiriesPanel from '../components/EnquiriesPanel';
import AnalyticsPanel from '../components/AnalyticsPanel';
import NewsletterSubscribersPanel from '../components/NewsletterSubscribersPanel';
import ThemeToggle from '../components/ThemeToggle';
import VoiceRecorder from '../components/VoiceRecorder';
import PublishCheckoutModal from '../components/PublishCheckoutModal';
import DomainPanel from '../components/DomainPanel';
import UserMenu from '../components/UserMenu';
import { getPreviewUrl as buildPreviewUrl, getLiveUrl, shareWebsite, verifiedCustomDomain } from '../utils/websiteUrls';
import { useNotify } from '../context/NotifyContext';
import usePageMeta from '../hooks/usePageMeta';

const DEFAULT_EDIT_WEBSITE_TAB: EditWebsiteTab = 'info';

function isEditWebsiteTab(value: string | null): value is EditWebsiteTab {
  return !!value && (EDIT_WEBSITE_TABS as string[]).includes(value);
}

interface WebsiteSection {
  _id: string;
  sectionType: string;
  layoutId: any;
  order: number;
  isVisible: boolean;
  contentId?: string;
  title?: string;
  subtitle?: string;
}

const EditWebsitePage: React.FC = () => {
  usePageMeta({ title: 'Edit website — OnTheWeb', index: false });

  const { websiteId } = useParams<{ websiteId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify, confirm } = useNotify();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab: EditWebsiteTab = isEditWebsiteTab(rawTab)
    ? (rawTab as EditWebsiteTab)
    : LEGACY_TAB_REDIRECTS[rawTab || ''] ?? DEFAULT_EDIT_WEBSITE_TAB;

  // Keeps the active tab in the URL (?tab=sections) so refreshing, sharing a
  // link, or using browser back/forward lands back on the same tab instead
  // of always resetting to "Website Info".
  const setActiveTab = (tab: EditWebsiteTab) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === DEFAULT_EDIT_WEBSITE_TAB) {
          next.delete('tab');
        } else {
          next.set('tab', tab);
        }
        return next;
      },
      { replace: true }
    );
  };
  const [editingContentSection, setEditingContentSection] = useState<WebsiteSection | null>(null);
  const [editingInfo, setEditingInfo] = useState(false);
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [addSectionStep, setAddSectionStep] = useState<'type' | 'prompt'>('type');
  const [showChangeLayoutModal, setShowChangeLayoutModal] = useState(false);
  const [showPublishConfirmModal, setShowPublishConfirmModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [sectionPrompt, setSectionPrompt] = useState('');
  const [promptEditSection, setPromptEditSection] = useState<WebsiteSection | null>(null);
  const [editSectionPrompt, setEditSectionPrompt] = useState('');
  const [editingSection, setEditingSection] = useState<WebsiteSection | null>(null);
  const [selectedSection, setSelectedSection] = useState<Section | null>(null);
  const [selectedLayoutId, setSelectedLayoutId] = useState<string>('');
  const [editingItem, setEditingItem] = useState<any>(null);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [websiteInfo, setWebsiteInfo] = useState({
    businessName: '',
    category: '',
    description: '',
    slug: '',
    logo: '' as string | undefined,
    favicon: '' as string | undefined,
    metaTitle: '',
    metaDescription: '',
    // Comma-separated in the form; split into the string[] the API wants on save.
    metaKeywords: '',
    status: 'draft' as 'draft' | 'published' | 'unpublished',
  });

  // Fetch website data
  const { data: website, isLoading } = useQuery({
    queryKey: ['website', websiteId],
    queryFn: () => websiteApi.getWebsiteById(websiteId!),
    enabled: !!websiteId,
  });

  // Fetch sections
  const { data: websiteSections = [], refetch: refetchSections } = useQuery({
    queryKey: ['websiteSections', websiteId],
    queryFn: () => websiteApi.getSections(websiteId!),
    enabled: !!websiteId,
  });

  // Fetch all available sections (master data)
  const { data: allSections = [] } = useQuery({
    queryKey: ['allSections'],
    queryFn: () => sectionApi.getAllSections(),
  });

  // Fetch layouts for selected section
  const { data: layoutsData, isLoading: layoutsLoading } = useQuery({
    queryKey: ['layouts', selectedSection?.sectionId],
    queryFn: () => sectionApi.getLayoutsForSection(selectedSection!.sectionId),
    enabled: !!selectedSection,
  });

  // Lightweight fetch just for the sidebar's unread-enquiries badge (kept in
  // sync with the Enquiries tab via the shared ['enquiries', websiteId] query key prefix).
  const { data: enquiriesBadgeData } = useQuery({
    queryKey: ['enquiries', websiteId, 1],
    queryFn: () => websiteApi.getEnquiries(websiteId!, 1, 20),
    enabled: !!websiteId,
  });

  useEffect(() => {
    if (website) {
      setWebsiteInfo({
        businessName: website.businessName,
        category: website.category || '',
        description: website.description || '',
        slug: website.slug,
        logo: website.logo,
        favicon: website.favicon,
        metaTitle: website.metaTitle || '',
        metaDescription: website.metaDescription || '',
        metaKeywords: (website.metaKeywords || []).join(', '),
        status: website.status,
      });
    }
  }, [website]);

  const publishToGoLive = (website as any)?.hasPublishedVersion
    ? ' Publish the website to make the changes live.'
    : '';

  // AI logo generation mutation — saves straight to the website server-side, then we just
  // refresh the query + local edit form so the new logo shows up immediately.
  const generateLogoMutation = useMutation({
    mutationFn: () => websiteApi.generateLogo(websiteId!),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['website', websiteId] });
      setWebsiteInfo((prev) => ({ ...prev, logo: result.logo }));
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to generate logo. Please try again.');
    },
  });

  // Update website mutation
  const updateWebsiteMutation = useMutation({
    mutationFn: (data: any) => websiteApi.updateWebsite(websiteId!, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['website', websiteId] });
      setEditingInfo(false);
      const isStatusOnly =
        Object.keys(variables).length === 1 && Object.prototype.hasOwnProperty.call(variables, 'status');
      if (isStatusOnly && variables.status === 'published') {
        notify.success('Website published successfully. It is now live.');
      } else {
        notify.success(
          'Website information saved successfully and updated on preview website. Publish the changes to make it live.'
        );
      }
    },
    onError: (error: any, variables) => {
      if (
        variables?.status === 'published' &&
        (error.response?.status === 402 || error.response?.data?.code === 'PAYMENT_REQUIRED')
      ) {
        setShowPublishConfirmModal(false);
        setShowCheckoutModal(true);
        return;
      }
      notify.error(error.response?.data?.message || 'Failed to update website');
    },
  });

  // Add section mutation — same generation path as website creation
  const addSectionMutation = useMutation({
    mutationFn: ({ sectionType, prompt }: { sectionType: string; prompt: string }) =>
      websiteApi.addSection(websiteId!, sectionType, prompt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websiteSections', websiteId] });
      closeAddSectionModal();
      notify.success(`Section added successfully.${publishToGoLive}`);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to add section');
    },
  });

  // Edit an existing section's content from a typed/spoken instruction
  const editSectionWithPromptMutation = useMutation({
    mutationFn: ({ sectionId, prompt }: { sectionId: string; prompt: string }) =>
      websiteApi.editSectionWithPrompt(websiteId!, sectionId, prompt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websiteSections', websiteId] });
      queryClient.invalidateQueries({ queryKey: ['sectionContent', websiteId] });
      queryClient.invalidateQueries({ queryKey: ['website', websiteId] });
      setPromptEditSection(null);
      setEditSectionPrompt('');
      setEditingContentSection(null);
      setEditingItem(null);
      setIsAddingItem(false);
      notify.success(`Section updated successfully.${publishToGoLive}`);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to update section from that instruction');
    },
  });

  // Remove section mutation
  const removeSectionMutation = useMutation({
    mutationFn: (sectionId: string) => websiteApi.removeSection(websiteId!, sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websiteSections', websiteId] });
      notify.success(`Section removed successfully.${publishToGoLive}`);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to remove section');
    },
  });

  // Change layout mutation
  const changeLayoutMutation = useMutation({
    mutationFn: ({ sectionId, layoutId }: { sectionId: string; layoutId: string }) =>
      websiteApi.updateSection(websiteId!, sectionId, { layoutId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websiteSections', websiteId] });
      setShowChangeLayoutModal(false);
      setEditingSection(null);
      setSelectedLayoutId('');
      notify.success(`Layout changed successfully.${publishToGoLive}`);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to change layout');
    },
  });

  // Reorder sections mutation
  const reorderSectionsMutation = useMutation({
    mutationFn: (sections: { sectionId: string; order: number }[]) =>
      websiteApi.reorderSections(websiteId!, sections),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websiteSections', websiteId] });
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to reorder sections');
    },
  });

  // Update single-record section content mutation
  const updateSingleContentMutation = useMutation({
    mutationFn: ({ sectionId, content }: { sectionId: string; content: any }) =>
      websiteApi.updateSingleContent(websiteId!, sectionId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['websiteSections', websiteId] });
      queryClient.invalidateQueries({ queryKey: ['sectionContent', websiteId] });
      notify.success(`Content saved successfully.${publishToGoLive}`);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to save content');
    },
  });

  // Create/update multi-record item mutation
  const saveItemMutation = useMutation({
    mutationFn: ({ sectionId, itemId, itemData }: { sectionId: string; itemId?: string; itemData: any }) => {
      if (itemId) {
        return websiteApi.updateContentItem(websiteId!, sectionId, itemId, itemData);
      } else {
        return websiteApi.createContentItem(websiteId!, sectionId, itemData);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sectionContent', websiteId] });
      setEditingItem(null);
      setIsAddingItem(false);
      notify.success(`Item saved successfully.${publishToGoLive}`);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to save item');
    },
  });

  // Delete content item mutation
  const deleteItemMutation = useMutation({
    mutationFn: ({ sectionId, itemId }: { sectionId: string; itemId: string }) =>
      websiteApi.deleteContentItem(websiteId!, sectionId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sectionContent', websiteId] });
      notify.success(`Item deleted successfully.${publishToGoLive}`);
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to delete item');
    },
  });

  // Fetch content schema for editing section
  const { data: contentSchema, isLoading: schemaLoading } = useQuery({
    queryKey: ['contentSchema', editingContentSection?.layoutId?.layoutId],
    queryFn: () => sectionApi.getContentSchema(editingContentSection!.layoutId.layoutId),
    enabled: !!editingContentSection?.layoutId?.layoutId,
  });

  // Fetch actual content for editing section (auto-detects single vs multi)
  const { data: sectionContentData, isLoading: contentLoading } = useQuery({
    queryKey: ['sectionContent', websiteId, editingContentSection?._id],
    queryFn: () => websiteApi.getSectionContent(websiteId!, editingContentSection!._id),
    enabled: !!editingContentSection?._id && !!websiteId,
  });

  // Determine if this is a single or multi-record section
  const isSingleRecord = sectionContentData?.isSingleRecord ?? true;
  const sectionContent = isSingleRecord ? sectionContentData?.content : null;
  const contentItems = !isSingleRecord ? sectionContentData?.items : [];

  const handleSaveInfo = () => {
    if (!websiteInfo.slug || websiteInfo.slug.trim().length < 3) {
      notify.error('Website URL slug must be at least 3 characters long.');
      return;
    }
    const { status: _status, metaKeywords, ...infoUpdates } = websiteInfo;
    updateWebsiteMutation.mutate({
      ...infoUpdates,
      metaKeywords: metaKeywords
        .split(',')
        .map((keyword) => keyword.trim())
        .filter(Boolean),
    });
  };

  // Normalizes slug input as the user types: lowercase, spaces -> hyphens,
  // strips anything that isn't a lowercase letter, number, or hyphen.
  const handleSlugChange = (value: string) => {
    const normalized = value
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-');
    setWebsiteInfo({ ...websiteInfo, slug: normalized });
  };

  const handlePublishClick = () => {
    if ((website as any)?.hasActivePayment) {
      setShowPublishConfirmModal(true);
      return;
    }
    setShowCheckoutModal(true);
  };

  const handlePublishConfirm = () => {
    updateWebsiteMutation.mutate(
      { status: 'published' },
      {
        onSuccess: () => {
          setShowPublishConfirmModal(false);
        },
      }
    );
  };

  const closeAddSectionModal = () => {
    setShowAddSectionModal(false);
    setAddSectionStep('type');
    setSelectedSection(null);
    setSectionPrompt('');
  };

  const handleAddSection = () => {
    if (!selectedSection || !sectionPrompt.trim()) {
      notify.error('Please select a section type and describe what you want');
      return;
    }
    addSectionMutation.mutate({
      sectionType: selectedSection.sectionId,
      prompt: sectionPrompt.trim(),
    });
  };

  const handleRemoveSection = async (sectionId: string) => {
    const ok = await confirm({
      title: 'Remove section',
      message: 'Are you sure you want to remove this section?',
      confirmLabel: 'Remove',
      danger: true,
    });
    if (ok) removeSectionMutation.mutate(sectionId);
  };

  const handleChangeLayout = () => {
    if (!editingSection || !selectedLayoutId) {
      notify.error('Please select a layout');
      return;
    }
    changeLayoutMutation.mutate({
      sectionId: editingSection._id,
      layoutId: selectedLayoutId,
    });
  };

  const handleMoveSection = (index: number, direction: 'up' | 'down') => {
    const sortedSections = [...websiteSections].sort((a, b) => a.order - b.order);
    
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === sortedSections.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = sortedSections[index];
    sortedSections[index] = sortedSections[newIndex];
    sortedSections[newIndex] = temp;

    const reorderedSections = sortedSections.map((section, idx) => ({
      sectionId: section._id,
      order: idx,
    }));

    reorderSectionsMutation.mutate(reorderedSections);
  };

  const getPreviewUrl = () => buildPreviewUrl(website?.slug || '');

  const handleShare = async () => {
    if (!website) return;
    try {
      const result = await shareWebsite({
        businessName: website.businessName,
        slug: website.slug,
        hasPublishedVersion: (website as any).hasPublishedVersion,
        customDomain: verifiedCustomDomain(website as any),
      });
      if (result === 'copied') {
        notify.success('Link copied to clipboard.');
      }
    } catch (error) {
      console.error('Failed to share website:', error);
      notify.error('Failed to share website');
    }
  };

  // Delete website mutation
  const deleteWebsiteMutation = useMutation({
    mutationFn: () => websiteApi.deleteWebsite(websiteId!),
    onSuccess: () => {
      navigate('/dashboard');
    },
    onError: (error: any) => {
      notify.error(error.response?.data?.message || 'Failed to delete website');
    },
  });

  const handleDeleteWebsite = async () => {
    if (!website) return;
    const ok = await confirm({
      title: 'Delete website',
      message: `Are you sure you want to delete "${website.businessName}"? This will permanently remove the website and all its content. This action cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    deleteWebsiteMutation.mutate();
  };

  // Get already added section types
  const addedSectionTypes = new Set(websiteSections.map((s: WebsiteSection) => s.sectionType));
  const availableSections = allSections.filter((s) => !addedSectionTypes.has(s.sectionId));

  if (isLoading) {
    return (
      <div className="nova-page flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-nova-sky"></div>
      </div>
    );
  }

  if (!website) {
    return (
      <div className="nova-page flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-nova-text mb-4">Website not found</h2>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-nova-sky hover:text-nova-sky2 font-medium"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="nova-page">
      {/* Header */}
      <nav className="nova-nav relative h-auto sm:h-[68px] border-b border-nova-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex flex-col gap-3 py-3 sm:flex-row sm:justify-between sm:items-center sm:h-16 sm:py-0">
            <div className="flex items-center space-x-3 min-w-0">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-nova-text2 hover:text-nova-text p-2 -ml-2 flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px]"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-nova-text truncate">{website.businessName}</h1>
                <p className="text-sm text-nova-text3">Editing website</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <span
                className={`px-3 py-1 text-sm font-semibold rounded-full ${
                  (website as any).hasPublishedVersion
                    ? 'status-published'
                    : 'status-draft'
                }`}
              >
                {(website as any).hasPublishedVersion ? 'Published' : 'Draft'}
              </span>
              <button
                onClick={() => window.open(getPreviewUrl(), '_blank')}
                className="px-4 py-2 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2 transition-colors"
              >
                Preview
              </button>
              {(website as any).hasPublishedVersion && (
                <button
                  onClick={() => window.open(getLiveUrl(website.slug, verifiedCustomDomain(website as any)), '_blank')}
                  className="px-4 py-2 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2 transition-colors"
                >
                  View Live
                </button>
              )}
              <button
                onClick={handleShare}
                className="px-4 py-2 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342a4 4 0 010-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a4 4 0 105.367-1.317 4 4 0 00-5.367 1.317zm0 9.316a4 4 0 105.368 1.317 4 4 0 00-5.368-1.317z" />
                </svg>
                Share
              </button>
              {((website as any).hasPublishedVersion && (website as any).hasUnpublishedChanges) ||
              !(website as any).hasPublishedVersion ? (
                <button
                  onClick={handlePublishClick}
                  disabled={updateWebsiteMutation.isPending}
                  className="px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-2 min-w-[120px] justify-center bg-nova-green hover:bg-nova-green/90 text-nova-bg disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {updateWebsiteMutation.isPending ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Publishing...
                    </>
                  ) : (
                    'Publish'
                  )}
                </button>
              ) : null}
              <ThemeToggle />
              <UserMenu />
            </div>
          </div>
        </div>
      </nav>

      {/* Body: left sidebar nav + main content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 md:pb-8 flex flex-col md:flex-row gap-6">
        <EditWebsiteSidebar
          activeTab={activeTab}
          onChange={setActiveTab}
          unreadEnquiriesCount={enquiriesBadgeData?.unreadCount || 0}
        />

        <div className="flex-1 min-w-0">
        {activeTab === 'info' && (
          <div className="nova-card p-4 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
              <h2 className="text-2xl font-bold text-nova-text">Website Information</h2>
              {!editingInfo ? (
                <button
                  onClick={() => setEditingInfo(true)}
                  className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors self-start sm:self-auto"
                >
                  Edit Info
                </button>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setEditingInfo(false);
                      setWebsiteInfo({
                        businessName: website.businessName,
                        category: website.category || '',
                        description: website.description || '',
                        slug: website.slug,
                        logo: website.logo,
                        favicon: website.favicon,
                        metaTitle: website.metaTitle || '',
                        metaDescription: website.metaDescription || '',
                        metaKeywords: (website.metaKeywords || []).join(', '),
                        status: website.status,
                      });
                    }}
                    className="px-4 py-2 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveInfo}
                    disabled={updateWebsiteMutation.isPending}
                    className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors disabled:opacity-50"
                  >
                    {updateWebsiteMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-nova-text2 mb-2">
                  Business Name
                </label>
                {editingInfo ? (
                  <input
                    type="text"
                    value={websiteInfo.businessName}
                    onChange={(e) => setWebsiteInfo({ ...websiteInfo, businessName: e.target.value })}
                    className="w-full px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30 focus:border-nova-sky"
                  />
                ) : (
                  <p className="text-nova-text">{website.businessName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-nova-text2 mb-2">
                  Logo / Image
                </label>
                {editingInfo ? (
                  <div className="space-y-2">
                    <ImageUpload
                      value={websiteInfo.logo || ''}
                      onChange={(url) => setWebsiteInfo({ ...websiteInfo, logo: url || undefined })}
                      label="Upload or enter URL"
                      folder="website-logos"
                      recommendedSize={{ width: 240, height: 80, note: 'wide, transparent PNG works best' }}
                    />
                    <button
                      type="button"
                      onClick={() => generateLogoMutation.mutate()}
                      disabled={generateLogoMutation.isPending}
                      className="flex items-center gap-2 px-4 py-2 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {generateLogoMutation.isPending ? (
                        <>
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Generating logo...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                          </svg>
                          Generate logo with AI
                        </>
                      )}
                    </button>
                    <p className="text-xs text-nova-text3">
                      Uses the business name, category, and current theme colors to create a matching logo.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    {website.logo ? (
                      <img src={website.logo} alt="Logo" className="h-16 w-auto object-contain border rounded" />
                    ) : (
                      <p className="text-nova-text3">No logo set</p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-nova-text2 mb-2">
                  Favicon
                </label>
                {editingInfo ? (
                  <div className="space-y-2">
                    {/* Square recommendation is what forces a 1:1 crop in ImageUpload,
                        which derives its crop aspect from these dimensions. */}
                    <ImageUpload
                      value={websiteInfo.favicon || ''}
                      onChange={(url) => setWebsiteInfo({ ...websiteInfo, favicon: url || undefined })}
                      label="Upload or enter URL"
                      folder="website-favicons"
                      recommendedSize={{ width: 512, height: 512, note: 'square' }}
                    />
                    <p className="text-xs text-nova-text3">
                      Shown in browser tabs and bookmarks. Optional — your logo is used when
                      this is empty, though a square image stays sharper at small sizes.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    {website.favicon ? (
                      <img
                        src={website.favicon}
                        alt="Favicon"
                        className="h-10 w-10 object-contain border rounded bg-nova-bg2"
                      />
                    ) : (
                      <p className="text-nova-text3">
                        {website.logo ? 'Using your logo' : 'No favicon set'}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-nova-text2 mb-2">
                  Category
                </label>
                {editingInfo ? (
                  <input
                    type="text"
                    value={websiteInfo.category}
                    onChange={(e) => setWebsiteInfo({ ...websiteInfo, category: e.target.value })}
                    className="w-full px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30 focus:border-nova-sky"
                  />
                ) : (
                  <p className="text-nova-text">{website.category || 'Not specified'}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-nova-text2 mb-2">
                  Description
                </label>
                {editingInfo ? (
                  <textarea
                    rows={4}
                    value={websiteInfo.description}
                    onChange={(e) => setWebsiteInfo({ ...websiteInfo, description: e.target.value })}
                    className="w-full px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30 focus:border-nova-sky"
                  />
                ) : (
                  <p className="text-nova-text">{website.description || 'No description'}</p>
                )}
              </div>

              <div className="pt-6 border-t border-nova-border">
                <h3 className="text-sm font-semibold text-nova-text mb-1">Search engines (SEO)</h3>
                <p className="text-sm text-nova-text3 mb-5">
                  This is what Google and other search engines show when your website appears in
                  results. Leave a field empty to fall back to your business name and description.
                  {publishToGoLive}
                </p>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-nova-text2 mb-2">
                      Meta title
                    </label>
                    {editingInfo ? (
                      <>
                        <input
                          type="text"
                          maxLength={70}
                          value={websiteInfo.metaTitle}
                          onChange={(e) => setWebsiteInfo({ ...websiteInfo, metaTitle: e.target.value })}
                          placeholder={website.businessName}
                          className="w-full px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30 focus:border-nova-sky"
                        />
                        <p className="text-xs text-nova-text3 mt-1">
                          {websiteInfo.metaTitle.length}/70 — search results usually cut off around 60 characters.
                        </p>
                      </>
                    ) : (
                      <p className={website.metaTitle ? 'text-nova-text' : 'text-nova-text3'}>
                        {website.metaTitle || `Using business name — ${website.businessName}`}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-nova-text2 mb-2">
                      Meta description
                    </label>
                    {editingInfo ? (
                      <>
                        <textarea
                          rows={3}
                          maxLength={200}
                          value={websiteInfo.metaDescription}
                          onChange={(e) => setWebsiteInfo({ ...websiteInfo, metaDescription: e.target.value })}
                          placeholder={website.description || 'A short summary of what your business offers'}
                          className="w-full px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30 focus:border-nova-sky"
                        />
                        <p className="text-xs text-nova-text3 mt-1">
                          {websiteInfo.metaDescription.length}/200 — search results usually cut off around 160 characters.
                        </p>
                      </>
                    ) : (
                      <p className={website.metaDescription ? 'text-nova-text' : 'text-nova-text3'}>
                        {website.metaDescription || 'Using the description above'}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-nova-text2 mb-2">
                      Keywords
                    </label>
                    {editingInfo ? (
                      <>
                        <input
                          type="text"
                          value={websiteInfo.metaKeywords}
                          onChange={(e) => setWebsiteInfo({ ...websiteInfo, metaKeywords: e.target.value })}
                          placeholder="bakery, birthday cakes, Noida"
                          className="w-full px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30 focus:border-nova-sky"
                        />
                        <p className="text-xs text-nova-text3 mt-1">
                          Optional. Separate with commas — the words customers would search for.
                        </p>
                      </>
                    ) : (
                      <p className={website.metaKeywords?.length ? 'text-nova-text' : 'text-nova-text3'}>
                        {website.metaKeywords?.length ? website.metaKeywords.join(', ') : 'None'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-nova-text2 mb-2">
                  Website URL slug
                </label>
                {editingInfo ? (
                  <div>
                    <div className="flex flex-col sm:flex-row sm:items-center rounded-lg border border-nova-border overflow-hidden focus-within:ring-2 focus-within:ring-nova-sky/30 focus-within:border-nova-sky">
                      <span className="px-3 py-2 bg-nova-bg2 text-nova-text3 text-xs sm:text-sm font-mono border-b sm:border-b-0 sm:border-r border-nova-border break-all">
                        {`${process.env.REACT_APP_PREVIEW_URL || 'http://localhost:3003/preview'}/`}
                      </span>
                      <input
                        type="text"
                        value={websiteInfo.slug}
                        onChange={(e) => handleSlugChange(e.target.value)}
                        className="flex-1 px-3 py-2 font-mono text-sm focus:outline-none min-w-0"
                        placeholder="my-business-name"
                      />
                    </div>
                    <p className="text-xs text-nova-text3 mt-1">
                      Lowercase letters, numbers, and hyphens only (min. 3 characters). This is used for both your preview and (once published) your live website address.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-nova-text3 mb-1">Preview URL</p>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <p className="text-nova-text font-mono text-sm break-all min-w-0">{getPreviewUrl()}</p>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(getPreviewUrl());
                            notify.success('URL copied to clipboard.');
                          }}
                          className="text-nova-sky hover:text-nova-sky2 p-2 -m-2 flex-shrink-0 self-start flex items-center justify-center min-h-[44px] min-w-[44px]"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-semibold text-nova-text3 mb-1">Live URL</p>
                      {(website as any).hasPublishedVersion ? (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <p className="text-nova-text font-mono text-sm break-all min-w-0">{getLiveUrl(website.slug, verifiedCustomDomain(website as any))}</p>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(getLiveUrl(website.slug, verifiedCustomDomain(website as any)));
                              notify.success('URL copied to clipboard.');
                            }}
                            className="text-nova-sky hover:text-nova-sky2 p-2 -m-2 flex-shrink-0 self-start flex items-center justify-center min-h-[44px] min-w-[44px]"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <p className="text-nova-text3 text-sm">Not available yet — publish your website to get a live address.</p>
                      )}
                    </div>
                    {(website as any).customDomain?.hostname && (
                      <div>
                        <p className="text-xs font-semibold text-nova-text3 mb-1">Custom domain</p>
                        <p className="text-nova-text font-mono text-sm break-all">
                          {(website as any).customDomain.hostname}
                          {(website as any).customDomain.status ? ` (${(website as any).customDomain.status})` : ''}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {websiteId && (
                <DomainPanel
                  websiteId={websiteId}
                  enabled={Boolean((website as any).hasPublishedVersion)}
                />
              )}

              <div className="pt-4 border-t border-nova-border">
                <p className="text-sm text-nova-text3">
                  Created: {new Date(website.createdAt).toLocaleString()}
                </p>
                {website.updatedAt && (
                  <p className="text-sm text-nova-text3">
                    Last updated: {new Date(website.updatedAt).toLocaleString()}
                  </p>
                )}
              </div>

              <div className="pt-6 border-t border-nova-border">
                <h3 className="text-sm font-semibold text-nova-red mb-1">Danger Zone</h3>
                <p className="text-sm text-nova-text3 mb-4">
                  Deleting this website permanently removes it along with all of its sections, content, and enquiries. This cannot be undone.
                </p>
                <button
                  onClick={handleDeleteWebsite}
                  disabled={deleteWebsiteMutation.isPending}
                  className="px-4 py-2 bg-nova-red/10 text-nova-red border border-nova-red/30 rounded-lg hover:bg-nova-red/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteWebsiteMutation.isPending ? 'Deleting...' : 'Delete Website'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'sections' && (
          <div className="space-y-6">
            <div className="nova-card p-4 sm:p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-nova-text">Website Sections</h2>
                  <p className="text-sm text-nova-text3 mt-1">
                    Add, reorder and remove sections here — use Edit Content to fill them in, or Edit with AI to regenerate a section.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 self-start sm:self-auto">
                  <button
                    onClick={() => {
                      setAddSectionStep('type');
                      setSelectedSection(null);
                      setSectionPrompt('');
                      setShowAddSectionModal(true);
                    }}
                    className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors"
                  >
                    + Add Section
                  </button>
                </div>
              </div>

              {websiteSections.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-nova-text3">No sections found. Click "Add Section" to get started!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {websiteSections
                    .sort((a: WebsiteSection, b: WebsiteSection) => a.order - b.order)
                    .map((section: WebsiteSection, index: number) => {
                      const sectionMeta = allSections.find((s) => s.sectionId === section.sectionType);
                      const sortedSections = [...websiteSections].sort((a, b) => a.order - b.order);
                      const hasContent = !!section.contentId;
                      return (
                        <div
                          key={section._id}
                          className="border border-nova-border rounded-lg p-4 hover:border-nova-sky/50 transition-colors"
                        >
                          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex items-start space-x-4 flex-1 min-w-0">
                              {/* Reorder buttons */}
                              <div className="flex flex-col space-y-1 flex-shrink-0">
                                <button
                                  onClick={() => handleMoveSection(index, 'up')}
                                  disabled={index === 0}
                                  className={`p-2.5 rounded flex items-center justify-center min-h-[44px] min-w-[44px] ${
                                    index === 0
                                      ? 'text-nova-text3 cursor-not-allowed'
                                      : 'text-nova-text2 hover:bg-nova-bg3'
                                  }`}
                                  title="Move up"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleMoveSection(index, 'down')}
                                  disabled={index === sortedSections.length - 1}
                                  className={`p-2.5 rounded flex items-center justify-center min-h-[44px] min-w-[44px] ${
                                    index === sortedSections.length - 1
                                      ? 'text-nova-text3 cursor-not-allowed'
                                      : 'text-nova-text2 hover:bg-nova-bg3'
                                  }`}
                                  title="Move down"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                              </div>

                              {/* Thumbnail */}
                              {sectionMeta?.thumbnail && (
                                <img
                                  src={sectionMeta.thumbnail}
                                  alt={sectionMeta.name}
                                  className="hidden sm:block w-24 h-24 object-cover rounded-lg border border-nova-border flex-shrink-0"
                                  onError={(e) => {
                                    e.currentTarget.src = 'https://via.placeholder.com/96x96?text=Section';
                                  }}
                                />
                              )}

                              {/* Section info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-semibold text-nova-text capitalize">
                                    {sectionMeta?.name || section.sectionType.replace(/_/g, ' ')}
                                  </h3>
                                  {hasContent && (
                                    <span className="px-2 py-0.5 text-xs font-semibold status-published rounded flex-shrink-0">
                                      ✓ Filled
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-nova-text2 mt-1">
                                  {sectionMeta?.description || 'No description'}
                                </p>
                                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-nova-text3">
                                  <span>Order: {section.order}</span>
                                  <span>Layout: {section.layoutId?.name || 'Unknown'}</span>
                                </div>
                              </div>
                            </div>

                            {/* Action buttons */}
                            <div className="flex flex-wrap items-center gap-2 sm:gap-1">
                              <button
                                onClick={() => {
                                  setEditingContentSection(section);
                                  setEditingItem(null);
                                  setIsAddingItem(false);
                                }}
                                className="px-3 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 min-h-[44px] transition-colors bg-nova-sky/10 text-nova-sky hover:bg-nova-sky/20"
                                title="Edit this section's content"
                              >
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit Content
                              </button>
                              <label className="flex items-center gap-2 min-h-[44px]">
                                <input
                                  type="checkbox"
                                  checked={section.isVisible}
                                  onChange={() => {
                                    websiteApi
                                      .updateSection(websiteId!, section._id, {
                                        isVisible: !section.isVisible,
                                      })
                                      .then(() => {
                                        refetchSections();
                                      });
                                  }}
                                  className="rounded border-nova-border text-nova-sky focus:ring-nova-sky/30 w-4 h-4"
                                />
                                <span className="text-sm text-nova-text2">Visible</span>
                              </label>
                              <button
                                onClick={() => {
                                  setPromptEditSection(section);
                                  setEditSectionPrompt('');
                                }}
                                className="px-3 py-2.5 rounded-lg text-sm font-semibold flex items-center gap-2 min-h-[44px] transition-colors bg-nova-sky/10 text-nova-sky hover:bg-nova-sky/20"
                                title="Regenerate this section and its content with AI"
                              >
                                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                                </svg>
                                Edit with AI
                              </button>
                              <button
                                onClick={() => {
                                  setEditingSection(section);
                                  const sectionData = allSections.find((s) => s.sectionId === section.sectionType);
                                  if (sectionData) {
                                    setSelectedSection(sectionData);
                                    setSelectedLayoutId(section.layoutId?._id || '');
                                    setShowChangeLayoutModal(true);
                                  }
                                }}
                                className="p-2.5 text-nova-sky hover:bg-nova-sky/10 rounded-lg flex items-center justify-center min-h-[44px] min-w-[44px]"
                                title="Change layout"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                                  />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleRemoveSection(section._id)}
                                className="p-2.5 text-nova-red hover:bg-nova-red/10 rounded-lg flex items-center justify-center min-h-[44px] min-w-[44px]"
                                title="Remove section"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Edit Content popup — same themed Modal shell as Add Section / Change Layout below,
            rather than the app's default page background, so it reads as one consistent
            editor no matter which section type it's showing. */}
        <Modal
          isOpen={!!editingContentSection}
          onClose={() => {
            setEditingContentSection(null);
            setEditingItem(null);
            setIsAddingItem(false);
          }}
          title={
            editingContentSection
              ? allSections.find((s) => s.sectionId === editingContentSection.sectionType)?.name ||
                editingContentSection.sectionType.replace(/_/g, ' ')
              : 'Edit Content'
          }
          maxWidthClassName="max-w-3xl"
        >
          {editingContentSection && (
            <div>
              <p className="text-sm text-nova-text2 -mt-2 mb-4">
                Layout: {editingContentSection.layoutId?.name}
              </p>
              <button
                type="button"
                onClick={() => {
                  setPromptEditSection(editingContentSection);
                  setEditSectionPrompt('');
                }}
                className="mb-6 px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2 bg-nova-sky/10 text-nova-sky hover:bg-nova-sky/20"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                </svg>
                Edit section and content with AI
              </button>

              {/* Section Metadata (Title & Subtitle) - Hide for sections that don't need it */}
              {!['hero', 'navigation', 'footer'].includes(editingContentSection.sectionType) && (
                <div className="mb-6 p-4 nova-card border border-nova-border">
                  <h4 className="font-semibold text-nova-text mb-4">Section Information</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-nova-text2 mb-2">
                        Section Title
                      </label>
                      <input
                        type="text"
                        value={editingContentSection.title || ''}
                        onChange={(e) => {
                          const updatedSection = { ...editingContentSection, title: e.target.value };
                          setEditingContentSection(updatedSection);
                        }}
                        placeholder="e.g., Our Services, Customer Reviews, etc."
                        className="w-full px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-nova-text2 mb-2">
                        Section Subtitle (Optional)
                      </label>
                      <input
                        type="text"
                        value={editingContentSection.subtitle || ''}
                        onChange={(e) => {
                          const updatedSection = { ...editingContentSection, subtitle: e.target.value };
                          setEditingContentSection(updatedSection);
                        }}
                        placeholder="e.g., What we offer, What our customers say, etc."
                        className="w-full px-4 py-2 border border-nova-border rounded-lg focus:ring-2 focus:ring-nova-sky/30"
                      />
                    </div>
                    <button
                      onClick={() => {
                        websiteApi.updateSection(websiteId!, editingContentSection._id, {
                          title: editingContentSection.title,
                          subtitle: editingContentSection.subtitle,
                        }).then(() => {
                          queryClient.invalidateQueries({ queryKey: ['website', websiteId] });
                          notify.success(`Section information updated.${publishToGoLive}`);
                        }).catch((err) => {
                          notify.error(err.response?.data?.message || 'Failed to update section');
                        });
                      }}
                      className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors"
                    >
                      Save Section Info
                    </button>
                  </div>
                </div>
              )}

              {schemaLoading || contentLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-nova-sky"></div>
                </div>
              ) : editingContentSection.sectionType === 'catalogue_with_categories' ? (
                <CategoryProductManager
                  websiteId={websiteId!}
                  section={editingContentSection}
                />
              ) : editingContentSection.sectionType === 'gallery_with_categories' ? (
                <GalleryCategoryManager
                  websiteId={websiteId!}
                  section={editingContentSection}
                />
              ) : contentSchema && contentSchema.fields.length > 0 ? (
                <>
                  {isSingleRecord ? (
                    <div>
                      <ContentEditor
                        fields={contentSchema.fields}
                        initialContent={sectionContent || {}}
                        onSave={(content) =>
                          updateSingleContentMutation.mutate({
                            sectionId: editingContentSection._id,
                            content,
                          })
                        }
                        isSaving={updateSingleContentMutation.isPending}
                      />
                    </div>
                  ) : (
                    <div>
                      {/* List of items */}
                      <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                        <h4 className="font-semibold text-nova-text">
                          {contentItems && contentItems.length > 0
                            ? `Existing Items (${contentItems.length})`
                            : 'Items'}
                        </h4>
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Spreadsheet round-trip only makes sense for a product
                              list; this toolbar is shared by every multi-record
                              section (services, FAQs, testimonials, ...). */}
                          {editingContentSection.sectionType === 'catalogue' && (
                            <ProductImportExport
                              websiteId={websiteId!}
                              sectionId={editingContentSection._id}
                              mode="plain"
                              label={editingContentSection.title || 'catalogue'}
                              onImported={() =>
                                queryClient.invalidateQueries({ queryKey: ['sectionContent', websiteId] })
                              }
                            />
                          )}
                          <button
                            onClick={() => {
                              setEditingItem(null);
                              setIsAddingItem(true);
                            }}
                            className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors"
                          >
                            + Add New Item
                          </button>
                        </div>
                      </div>
                      {editingContentSection.sectionType === 'catalogue' && (
                        <p className="text-xs text-nova-text3 mb-4">
                          Export to edit your products in Excel or Google Sheets, then import the same file back.
                          Keep a product's id to update it, or leave the id blank to add a new one.
                        </p>
                      )}
                      {contentItems && contentItems.length > 0 ? (
                        <div className="space-y-3 mb-6">
                          {contentItems.map((item: any) => (
                            <div key={item._id} className="p-4 border border-nova-border rounded-lg bg-nova-bg3 hover:border-nova-border2 transition-shadow">
                              <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-nova-text">{item.name || item.title || item.question || 'Item'}</p>
                                  {item.description && (
                                    <p className="text-sm text-nova-text2 mt-1">{item.description.substring(0, 100)}...</p>
                                  )}
                                  {item.content && (
                                    <p className="text-sm text-nova-text2 mt-1">{item.content.substring(0, 100)}...</p>
                                  )}
                                </div>
                                <div className="flex gap-2 sm:ml-4 self-end sm:self-auto">
                                  <button
                                    onClick={() => {
                                      setIsAddingItem(false);
                                      setEditingItem(item);
                                    }}
                                    className="px-3 py-1.5 text-sm btn-primary transition-colors"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={async () => {
                                      const ok = await confirm({
                                        title: 'Delete item',
                                        message: 'Are you sure you want to delete this item?',
                                        confirmLabel: 'Delete',
                                        danger: true,
                                      });
                                      if (!ok) return;
                                      deleteItemMutation.mutate({
                                        sectionId: editingContentSection._id,
                                        itemId: item._id,
                                      });
                                    }}
                                    className="px-3 py-1.5 text-sm bg-nova-red text-white rounded hover:bg-nova-red/90 transition-colors"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 bg-nova-bg2 rounded-lg mb-6">
                          <p className="text-nova-text3">No items yet. Click "Add New Item" to create one.</p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <p className="text-nova-text3">No content fields defined for this layout yet.</p>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Add/Edit multi-record item popup */}
        <Modal
          isOpen={(isAddingItem || !!editingItem) && !!editingContentSection && !!contentSchema}
          onClose={() => {
            setEditingItem(null);
            setIsAddingItem(false);
          }}
          title={editingItem ? 'Edit Item' : 'Add New Item'}
        >
          {editingContentSection && contentSchema && (
            <ContentEditor
              key={editingItem?._id || 'new'}
              fields={contentSchema.fields}
              initialContent={editingItem || {}}
              onSave={(itemData) =>
                saveItemMutation.mutate({
                  sectionId: editingContentSection._id,
                  itemId: editingItem?._id,
                  itemData,
                })
              }
              isSaving={saveItemMutation.isPending}
            />
          )}
        </Modal>

        {activeTab === 'theme' && websiteId && (
          <ThemeEditor websiteId={websiteId} previewUrl={getPreviewUrl()} />
        )}

        {activeTab === 'analytics' && websiteId && <AnalyticsPanel websiteId={websiteId} />}

        {activeTab === 'enquiries' && websiteId && <EnquiriesPanel websiteId={websiteId} />}
        {activeTab === 'newsletter' && websiteId && <NewsletterSubscribersPanel websiteId={websiteId} />}
        </div>
      </div>

      {/* Publish Confirmation Modal */}
      {showPublishConfirmModal && (
        <div className="nova-modal-overlay">
          <div className="nova-card shadow-nova-lg max-w-md w-full p-4 sm:p-6">
            <h3 className="text-xl font-bold text-nova-text mb-2">Publish Website?</h3>
            <p className="text-nova-text2 mb-6">
              Your website will go live and be visible to visitors at your website URL.
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
              <button
                onClick={() => setShowPublishConfirmModal(false)}
                disabled={updateWebsiteMutation.isPending}
                className="px-4 py-2 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePublishConfirm}
                disabled={updateWebsiteMutation.isPending}
                className="px-4 py-2 rounded-lg font-semibold text-white transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700"
              >
                {updateWebsiteMutation.isPending ? (
                  <>
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Publishing...
                  </>
                ) : (
                  'Yes, Publish'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCheckoutModal && websiteId && (
        <PublishCheckoutModal
          websiteId={websiteId}
          onClose={() => setShowCheckoutModal(false)}
          onPublished={(result) => {
            setShowCheckoutModal(false);
            queryClient.invalidateQueries({ queryKey: ['website', websiteId] });
            const qs = result.invoiceId ? `?invoiceId=${result.invoiceId}` : '';
            navigate(`/website/${websiteId}/published${qs}`);
          }}
        />
      )}

      {/* Add Section Modal: type → prompt/voice, then generate like website creation */}
      {showAddSectionModal && (
        <div className="nova-modal-overlay p-0 sm:p-4">
          <div className="nova-card shadow-nova-lg max-w-4xl w-full h-full sm:h-auto max-h-full sm:max-h-[90vh] sm:rounded-2xl rounded-none overflow-hidden flex flex-col">
            <div className="p-4 sm:p-6 border-b border-nova-border flex-shrink-0">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-nova-text">Add Section</h3>
                  <p className="text-sm text-nova-text2 mt-1">
                    {addSectionStep === 'type' ? 'Step 1 of 2 — choose a section type' : 'Step 2 of 2 — describe what to generate'}
                  </p>
                </div>
                <button
                  onClick={closeAddSectionModal}
                  disabled={addSectionMutation.isPending}
                  className="text-nova-text3 hover:text-nova-text2 p-2 -m-2 flex items-center justify-center min-h-[44px] min-w-[44px] disabled:opacity-50"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              {addSectionStep === 'type' ? (
                <>
                  <p className="text-nova-text2 mb-4">Select a section type:</p>
                  {availableSections.length === 0 ? (
                    <p className="text-nova-text3 text-center py-12">All available section types are already on this website.</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {availableSections.map((section) => (
                        <button
                          key={section._id}
                          type="button"
                          onClick={() => setSelectedSection(section)}
                          className={`p-4 border-2 rounded-lg transition-colors text-left ${
                            selectedSection?.sectionId === section.sectionId
                              ? 'border-nova-sky bg-nova-sky/10'
                              : 'border-nova-border hover:border-nova-sky'
                          }`}
                        >
                          {section.thumbnail && (
                            <img
                              src={section.thumbnail}
                              alt={section.name}
                              className="w-full h-32 object-cover rounded-lg mb-3"
                              onError={(e) => {
                                e.currentTarget.src = 'https://via.placeholder.com/200x128?text=Section';
                              }}
                            />
                          )}
                          <h4 className="font-semibold text-nova-text">{section.name}</h4>
                          <p className="text-sm text-nova-text2 mt-1">{section.description}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-nova-text mb-2">{selectedSection?.name}</h4>
                    <p className="text-nova-text2">{selectedSection?.description}</p>
                  </div>
                  <p className="text-sm text-nova-text2 mb-4">
                    Describe this section in text or by voice — the same generation used when creating the website
                    will fill it in. Speak in English or Hindi.
                  </p>
                  <VoiceRecorder
                    onTranscribed={(text) =>
                      setSectionPrompt((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text))
                    }
                    disabled={addSectionMutation.isPending}
                  />
                  <textarea
                    value={sectionPrompt}
                    onChange={(e) => setSectionPrompt(e.target.value)}
                    rows={4}
                    placeholder='e.g. "A pricing section with Basic, Standard and Premium gym memberships"'
                    className="nova-input w-full resize-none mt-4"
                    disabled={addSectionMutation.isPending}
                  />
                </>
              )}
            </div>

            <div className="p-4 sm:p-6 border-t border-nova-border flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 flex-shrink-0">
              {addSectionStep === 'prompt' && (
                <button
                  type="button"
                  onClick={() => setAddSectionStep('type')}
                  disabled={addSectionMutation.isPending}
                  className="px-4 py-2 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2 disabled:opacity-50 sm:mr-auto"
                >
                  Back
                </button>
              )}
              <button
                type="button"
                onClick={closeAddSectionModal}
                disabled={addSectionMutation.isPending}
                className="px-4 py-2 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2 disabled:opacity-50"
              >
                Cancel
              </button>
              {addSectionStep === 'type' ? (
                <button
                  type="button"
                  onClick={() => setAddSectionStep('prompt')}
                  disabled={!selectedSection}
                  className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleAddSection}
                  disabled={!selectedSection || !sectionPrompt.trim() || addSectionMutation.isPending}
                  className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addSectionMutation.isPending ? 'Generating...' : 'Create Section'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Change Layout Modal */}
      {showChangeLayoutModal && editingSection && selectedSection && (
        <div className="nova-modal-overlay p-0 sm:p-4">
          <div className="nova-card shadow-nova-lg max-w-4xl w-full h-full sm:h-auto max-h-full sm:max-h-[90vh] sm:rounded-2xl rounded-none overflow-hidden flex flex-col">
            <div className="p-4 sm:p-6 border-b border-nova-border flex-shrink-0">
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0">
                  <h3 className="text-xl sm:text-2xl font-bold text-nova-text">Change Layout</h3>
                  <p className="text-sm text-nova-text2 mt-1">
                    {selectedSection.name} - Select a different layout
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowChangeLayoutModal(false);
                    setEditingSection(null);
                    setSelectedSection(null);
                    setSelectedLayoutId('');
                  }}
                  className="text-nova-text3 hover:text-nova-text2 p-2 -m-2 flex-shrink-0 flex items-center justify-center min-h-[44px] min-w-[44px]"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto flex-1">
              {layoutsLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-nova-sky"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {layoutsData?.layouts.map((layout: Layout) => (
                    <button
                      key={layout._id}
                      onClick={() => setSelectedLayoutId(layout._id)}
                      className={`p-4 border-2 rounded-lg transition-all text-left ${
                        selectedLayoutId === layout._id
                          ? 'border-nova-sky bg-nova-sky/10'
                          : 'border-nova-border hover:border-nova-sky/50'
                      }`}
                    >
                      {layout.thumbnail && (
                        <img
                          src={layout.thumbnail}
                          alt={layout.name}
                          className="w-full h-40 object-cover rounded-lg mb-3"
                          onError={(e) => {
                            e.currentTarget.src = 'https://via.placeholder.com/300x160?text=Layout';
                          }}
                        />
                      )}
                      <h5 className="font-semibold text-nova-text">{layout.name}</h5>
                      <p className="text-sm text-nova-text2 mt-1">{layout.description}</p>
                      {editingSection.layoutId?._id === layout._id && (
                        <span className="inline-block mt-2 px-2 py-1 text-xs font-semibold status-published rounded">
                          Current Layout
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 sm:p-6 border-t border-nova-border flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 flex-shrink-0">
              <button
                onClick={() => {
                  setShowChangeLayoutModal(false);
                  setEditingSection(null);
                  setSelectedSection(null);
                  setSelectedLayoutId('');
                }}
                className="px-4 py-2 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2"
              >
                Cancel
              </button>
              <button
                onClick={handleChangeLayout}
                disabled={!selectedLayoutId || changeLayoutMutation.isPending}
                className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {changeLayoutMutation.isPending ? 'Changing...' : 'Change Layout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit section + content with AI — same generation path as website creation */}
      <Modal
        isOpen={!!promptEditSection}
        onClose={() => {
          if (editSectionWithPromptMutation.isPending) return;
          setPromptEditSection(null);
          setEditSectionPrompt('');
        }}
        title={`Edit "${
          promptEditSection
            ? allSections.find((s) => s.sectionId === promptEditSection.sectionType)?.name ||
              promptEditSection.sectionType.replace(/_/g, ' ')
            : ''
        }" with AI`}
        maxWidthClassName="max-w-2xl"
      >
        <div className="space-y-4">
          <p className="text-sm text-nova-text2">
            Describe how this section and its content should read. The same generator used when creating the website
            will rewrite the section title and body. Speak in English or Hindi.
          </p>
          <VoiceRecorder
            onTranscribed={(text) =>
              setEditSectionPrompt((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text))
            }
            disabled={editSectionWithPromptMutation.isPending}
          />
          <textarea
            value={editSectionPrompt}
            onChange={(e) => setEditSectionPrompt(e.target.value)}
            rows={4}
            placeholder='e.g. "Make this a warm welcome for a family bakery, with a heading about fresh bread every morning"'
            className="nova-input w-full resize-none"
            disabled={editSectionWithPromptMutation.isPending}
          />
        </div>
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 mt-6">
          <button
            onClick={() => {
              setPromptEditSection(null);
              setEditSectionPrompt('');
            }}
            disabled={editSectionWithPromptMutation.isPending}
            className="px-4 py-2 border border-nova-border text-nova-text2 rounded-lg hover:bg-nova-bg2 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              promptEditSection &&
              editSectionWithPromptMutation.mutate({ sectionId: promptEditSection._id, prompt: editSectionPrompt.trim() })
            }
            disabled={!editSectionPrompt.trim() || editSectionWithPromptMutation.isPending}
            className="px-4 py-2 btn-primary rounded-lg hover:bg-nova-sky2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {editSectionWithPromptMutation.isPending ? 'Generating...' : 'Update Section'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default EditWebsitePage;
